import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import {
  getAdminRestaurantsList,
  runQuantityAudit,
  runQuantityAuditAllRestaurants,
  runEmailAccuracyAudit,
  exportRestaurantData,
  lookupOrderByNumber,
  scanDuplicateProducts,
  type AdminRestaurant,
  type QuantityAuditResult,
  type GroupedQuantityAuditResult,
  type EmailAccuracyAuditResult,
  type DuplicateScanResult,
  type OrderLookupResult,
} from '../../../services/admin/adminService';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { MoetDuplicatesCard } from './MoetDuplicatesCard';
import { GmailIdCopy } from '../orderHistory/GmailCopy';
import { OrderLookupFactuurBlock, OrderLookupProductItem } from './testing/shared/OrderLookupBlocks';
import { calculateProductTotal } from '../../../utils/platformUtils';
import { EmailAccuracyAuditView } from './testing/emailAudit/EmailAccuracyAuditView';
import { QuantityAuditView } from './testing/quantityAudit/QuantityAuditView';
import { QuantityAuditAllView } from './testing/quantityAudit/QuantityAuditAllView';
import { DuplicateProductsView } from './testing/duplicates/DuplicateProductsView';
import { AICheckerView } from './testing/aiChecker/AICheckerView';

type AdminTestingView = 'export' | 'audit' | 'aiChecker';

const VIEW_TITLES: Record<AdminTestingView, string> = {
  export: 'Data Export',
  audit: 'Testing & Audit',
  aiChecker: 'AI Checker',
};

const AdminTestingPage = ({ view = 'audit' }: { view?: AdminTestingView }) => {
  // Super-admin tooling is always English regardless of the app's selected language.
  const { i18n } = useTranslation();
  // getFixedT returns a new function identity every call — memoize it, or effects
  // that depend on `t` re-run on every render (infinite fetch loop).
  const t = useMemo(() => i18n.getFixedT('en'), [i18n]);
  const [restaurants, setRestaurants] = useState<AdminRestaurant[]>([]);
  const [loading, setLoading] = useState(true);

  // Order lookup state
  const [orderLookupQuery, setOrderLookupQuery] = useState('');
  const [orderLookupLoading, setOrderLookupLoading] = useState(false);
  const [orderLookupResults, setOrderLookupResults] = useState<OrderLookupResult[] | null>(null);
  const [orderLookupError, setOrderLookupError] = useState<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  // Email accuracy audit state (unified: processed correctness + unprocessed classification)
  const [emailAuditAccount, setEmailAuditAccount] = useState('cloud64');
  const [emailAuditStartDate, setEmailAuditStartDate] = useState('');
  const [emailAuditEndDate, setEmailAuditEndDate] = useState('');
  const [accuracyAuditLoading, setAccuracyAuditLoading] = useState(false);
  const [accuracyAuditResult, setAccuracyAuditResult] = useState<EmailAccuracyAuditResult | null>(null);
  const [accuracyAuditError, setAccuracyAuditError] = useState<string | null>(null);

  // Quantity audit state
  const [auditRestaurantId, setAuditRestaurantId] = useState('');
  const [auditSearchQuery, setAuditSearchQuery] = useState('');
  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<QuantityAuditResult | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  // Cross-restaurant quantity audit state
  const [auditAllLoading, setAuditAllLoading] = useState(false);
  const [auditAllResult, setAuditAllResult] = useState<GroupedQuantityAuditResult | null>(null);
  const [auditAllError, setAuditAllError] = useState<string | null>(null);

  // Duplicate products state
  const [dupRestaurantId, setDupRestaurantId] = useState('');
  const [dupSearchQuery, setDupSearchQuery] = useState('');
  const [dupLoading, setDupLoading] = useState(false);
  const [dupResult, setDupResult] = useState<DuplicateScanResult | null>(null);
  const [dupError, setDupError] = useState<string | null>(null);

  // Data export state
  const [exportRestaurantId, setExportRestaurantId] = useState('');
  const [exportSearchQuery, setExportSearchQuery] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportMessage, setExportMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const list = await getAdminRestaurantsList();
        setRestaurants(list);
      } catch (err) {
        console.error('Failed to load restaurants:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const auditFilteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(auditSearchQuery.toLowerCase()) ||
    r.ownerEmail.toLowerCase().includes(auditSearchQuery.toLowerCase())
  );

  const exportFilteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(exportSearchQuery.toLowerCase()) ||
    r.ownerEmail.toLowerCase().includes(exportSearchQuery.toLowerCase())
  );

  const dupFilteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(dupSearchQuery.toLowerCase()) ||
    r.ownerEmail.toLowerCase().includes(dupSearchQuery.toLowerCase())
  );

  const handleOrderLookup = async () => {
    const trimmed = orderLookupQuery.trim();
    if (!trimmed) return;
    try {
      setOrderLookupLoading(true);
      setOrderLookupError(null);
      setOrderLookupResults(null);
      setExpandedOrders(new Set());
      const results = await lookupOrderByNumber(trimmed);
      setOrderLookupResults(results);
    } catch (err: any) {
      setOrderLookupError(err.message || 'Failed to lookup order');
    } finally {
      setOrderLookupLoading(false);
    }
  };

  const handleAccuracyAudit = async () => {
    if (!emailAuditStartDate || !emailAuditEndDate) return;
    try {
      setAccuracyAuditLoading(true);
      setAccuracyAuditError(null);
      setAccuracyAuditResult(null);
      const result = await runEmailAccuracyAudit(emailAuditAccount, emailAuditStartDate, emailAuditEndDate);
      setAccuracyAuditResult(result);
    } catch (err: any) {
      setAccuracyAuditError(err.message || t('adminTesting.accuracyAudit.error'));
    } finally {
      setAccuracyAuditLoading(false);
    }
  };

  const handleRunAudit = async () => {
    if (!auditRestaurantId || !auditStartDate || !auditEndDate) return;
    try {
      setAuditLoading(true);
      setAuditError(null);
      setAuditResult(null);
      const result = await runQuantityAudit(auditRestaurantId, auditStartDate, auditEndDate);
      setAuditResult(result);
    } catch (err: any) {
      setAuditError(err.message || 'Failed to run quantity audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleRunAuditAll = async () => {
    if (!auditStartDate || !auditEndDate) return;
    try {
      setAuditAllLoading(true);
      setAuditAllError(null);
      setAuditAllResult(null);
      const result = await runQuantityAuditAllRestaurants(auditStartDate, auditEndDate);
      setAuditAllResult(result);
    } catch (err: any) {
      setAuditAllError(err.message || t('adminTesting.qtyAuditAll.error'));
    } finally {
      setAuditAllLoading(false);
    }
  };

  const handleScanDuplicates = async (restaurantId?: string) => {
    try {
      setDupLoading(true);
      setDupError(null);
      setDupResult(null);
      const result = await scanDuplicateProducts(restaurantId);
      setDupResult(result);
    } catch (err: any) {
      setDupError(err.message || 'Failed to scan for duplicate products');
    } finally {
      setDupLoading(false);
    }
  };

  const handleExport = async () => {
    if (!exportRestaurantId || !exportStartDate || !exportEndDate) return;
    try {
      setExportLoading(true);
      setExportMessage(null);
      const result = await exportRestaurantData(exportRestaurantId, exportStartDate, exportEndDate);
      // Trigger a plain-text (.txt) file download in the browser.
      const blob = new Blob([result.markdown], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportMessage({ type: 'success', text: `Exported ${result.restaurantName} (${result.filename})` });
    } catch (err: any) {
      setExportMessage({ type: 'error', text: err.message || 'Failed to export data' });
    } finally {
      setExportLoading(false);
    }
  };

  const toggleOrderExpanded = (id: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={secondaryText}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <h2 className={cn(primaryText, 'text-lg', semiBoldText)}>{VIEW_TITLES[view]}</h2>

        {view === 'audit' && (<>
        {/* Order Lookup Card */}
        <div className={cn(cardClass, 'p-6')}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={cn(primaryText, semiBoldText)}>Order Lookup</h3>
              <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
                Find an order by exact order number — shows platform, restaurant, owner, and totals
              </p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <input
              type="text"
              placeholder="Order number (exact match)"
              value={orderLookupQuery}
              onChange={(e) => setOrderLookupQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleOrderLookup(); }}
              className={cn(
                'flex-1 px-3 py-2 rounded-lg border',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
            <button
              onClick={handleOrderLookup}
              disabled={!orderLookupQuery.trim() || orderLookupLoading}
              className={cn(
                'px-4 py-2 rounded-lg transition-colors',
                'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                smallTextSize, semiBoldText
              )}
            >
              {orderLookupLoading ? 'Searching...' : 'Search'}
            </button>
          </div>

          {orderLookupError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4">
              {orderLookupError}
            </div>
          )}

          {orderLookupResults && orderLookupResults.length === 0 && (
            <div className={cn(secondaryText, smallTextSize)}>
              No orders found with that order number.
            </div>
          )}

          {orderLookupResults && orderLookupResults.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className={cn('border-b border-gray-200 dark:border-gray-700', secondaryText, 'text-xs uppercase tracking-wide')}>
                    <th className="py-2 pr-3">Order #</th>
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Platform</th>
                    <th className="py-2 pr-3">Restaurant</th>
                    <th className="py-2 pr-3">Owner</th>
                    <th className="py-2 pr-3 text-right">Total</th>
                    <th className="py-2 pr-3 text-right">Total w/ tax</th>
                    <th className="py-2 pr-3 text-right">Product sum</th>
                    <th className="py-2 pr-3 text-right">Email / File</th>
                  </tr>
                </thead>
                <tbody>
                  {orderLookupResults.map(r => {
                    const isExpanded = expandedOrders.has(r.id);
                    return (
                    <Fragment key={r.id}>
                    <tr
                      onClick={() => toggleOrderExpanded(r.id)}
                      className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
                    >
                      <td className={cn('py-2 pr-3', primaryText, smallTextSize, semiBoldText)}>
                        <div className="flex items-center gap-1.5">
                          {isExpanded
                            ? <ChevronDown className="w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark" />
                            : <ChevronRight className="w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark" />}
                          {r.orderNumber}
                          {r.facturen.length > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500 dark:bg-amber-500 text-white dark:text-white text-[10px] font-semibold">
                              {r.facturen.length > 1
                                ? t('adminTesting.orderLookup.factuur.badgeCount', { count: r.facturen.length })
                                : t('adminTesting.orderLookup.factuur.badge')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={cn('py-2 pr-3', primaryText, smallTextSize)}>
                        {new Date(r.orderDate).toLocaleDateString('nl-NL')}
                      </td>
                      <td className={cn('py-2 pr-3', primaryText, smallTextSize)}>{r.platform}</td>
                      <td className={cn('py-2 pr-3', primaryText, smallTextSize)}>{r.restaurantName}</td>
                      <td className={cn('py-2 pr-3', smallTextSize)}>
                        <div className={primaryText}>{r.userName ?? '—'}</div>
                        <div className={cn(secondaryText, 'text-xs')}>{r.userEmail}</div>
                      </td>
                      <td className={cn('py-2 pr-3 text-right', primaryText, smallTextSize)}>
                        {r.total.toFixed(2)}
                      </td>
                      <td className={cn('py-2 pr-3 text-right', primaryText, smallTextSize)}>
                        {r.totalWithTaxes !== null ? r.totalWithTaxes.toFixed(2) : '—'}
                      </td>
                      <td className={cn('py-2 pr-3 text-right', primaryText, smallTextSize)}>
                        {r.productSum.toFixed(2)}
                      </td>
                      <td className="py-2 pr-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-3">
                          <GmailIdCopy gmailId={r.gmailId ?? undefined} />
                          {r.sourceFileUrl ? (
                            <a
                              href={r.sourceFileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm text-blue-600 dark:text-textBlue-dark hover:underline"
                            >
                              <FileText className="w-4 h-4" />
                              See file
                            </a>
                          ) : (
                            <span className={cn(secondaryText, 'text-xs')}>no file</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={9} className="px-3 py-3 space-y-3">
                          {r.facturen.length === 0 ? (
                            <div className={cn(secondaryText, 'text-xs')}>
                              {t('adminTesting.orderLookup.factuur.none')}
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {r.facturen.map(f => (
                                <OrderLookupFactuurBlock
                                  key={f.factuurOrderId}
                                  factuur={f}
                                  platform={r.platform}
                                  orderTotal={r.total}
                                  t={t}
                                />
                              ))}
                            </div>
                          )}
                          {r.products.length === 0 ? (
                            <div className={cn(secondaryText, smallTextSize)}>No products on this order.</div>
                          ) : (
                            <div className="space-y-2">
                              <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>
                                {r.products.length} product{r.products.length === 1 ? '' : 's'}
                              </div>
                              {[...r.products]
                                .sort((a, b) => calculateProductTotal(b as any, r.platform) - calculateProductTotal(a as any, r.platform))
                                .map((p, i) => (
                                  <OrderLookupProductItem key={p.articleId || i} product={p} platform={r.platform} />
                                ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Email Accuracy Audit Card */}
        <div className={cn(cardClass, 'p-6')}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={cn(primaryText, semiBoldText)}>{t('adminTesting.accuracyAudit.title')}</h3>
              <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
                {t('adminTesting.accuracyAudit.desc')}
              </p>
            </div>
          </div>

          <div className="mb-4">
            {/* Account + date range */}
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>Mailbox</label>
                <select
                  value={emailAuditAccount}
                  onChange={(e) => setEmailAuditAccount(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                >
                  <option value="cloud64">culinarycloud64@gmail.com</option>
                  <option value="cloud63">culinarycloud63@gmail.com</option>
                </select>
              </div>
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>Start Date</label>
                <input
                  type="date"
                  value={emailAuditStartDate}
                  onChange={(e) => setEmailAuditStartDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>End Date</label>
                <input
                  type="date"
                  value={emailAuditEndDate}
                  onChange={(e) => setEmailAuditEndDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
            </div>

            <button
              onClick={handleAccuracyAudit}
              disabled={!emailAuditStartDate || !emailAuditEndDate || accuracyAuditLoading}
              className={cn(
                'px-4 py-2 rounded-lg transition-colors',
                'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                smallTextSize, semiBoldText
              )}
            >
              {accuracyAuditLoading ? t('adminTesting.accuracyAudit.running') : t('adminTesting.accuracyAudit.button')}
            </button>
          </div>

          {accuracyAuditError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4">
              {accuracyAuditError}
            </div>
          )}

          {accuracyAuditResult && (
            <EmailAccuracyAuditView result={accuracyAuditResult} t={t} />
          )}
        </div>

        {/* Quantity Info Audit Card */}
        <div className={cn(cardClass, 'p-6')}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={cn(primaryText, semiBoldText)}>Quantity Info Audit</h3>
              <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
                AI-powered check for incorrect product quantity info (weightString, stdAmount, stdUnit mismatches)
              </p>
            </div>
          </div>

          {/* Restaurant selector */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search restaurants..."
              value={auditSearchQuery}
              onChange={(e) => setAuditSearchQuery(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border mb-2',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
              {auditFilteredRestaurants.map(r => (
                <button
                  key={r.id}
                  onClick={() => setAuditRestaurantId(r.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 transition-colors',
                    smallTextSize,
                    auditRestaurantId === r.id
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  <div className={cn(primaryText, semiBoldText)}>{r.name}</div>
                  <div className={cn(secondaryText, 'text-xs')}>{r.ownerEmail}</div>
                </button>
              ))}
            </div>

            {/* Date range */}
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>Start Date</label>
                <input
                  type="date"
                  value={auditStartDate}
                  onChange={(e) => setAuditStartDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>End Date</label>
                <input
                  type="date"
                  value={auditEndDate}
                  onChange={(e) => setAuditEndDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleRunAudit}
                disabled={!auditRestaurantId || !auditStartDate || !auditEndDate || auditLoading}
                className={cn(
                  'px-4 py-2 rounded-lg transition-colors',
                  'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  smallTextSize, semiBoldText
                )}
              >
                {auditLoading ? 'Auditing...' : 'Audit Quantity Info'}
              </button>
              <button
                onClick={handleRunAuditAll}
                disabled={!auditStartDate || !auditEndDate || auditAllLoading}
                title={t('adminTesting.qtyAuditAll.hint')}
                className={cn(
                  'px-4 py-2 rounded-lg transition-colors',
                  'bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 text-white dark:text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  smallTextSize, semiBoldText
                )}
              >
                {auditAllLoading ? t('adminTesting.qtyAuditAll.running') : t('adminTesting.qtyAuditAll.button')}
              </button>
            </div>
          </div>

          {/* Error */}
          {auditError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4">
              {auditError}
            </div>
          )}

          {/* Results */}
          {auditResult && (
            <QuantityAuditView
              result={auditResult}
              setResult={setAuditResult}
              t={t}
              restaurantName={restaurants.find(r => r.id === auditRestaurantId)?.name ?? ''}
            />
          )}

          {auditAllError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4 mt-4">
              {auditAllError}
            </div>
          )}

          {auditAllResult && (
            <div className="mt-6">
              <QuantityAuditAllView result={auditAllResult} t={t} />
            </div>
          )}
        </div>

        {/* Duplicate Products Card */}
        <div className={cn(cardClass, 'p-6')}>
          <div className="mb-4">
            <h3 className={cn(primaryText, semiBoldText)}>Duplicate Products</h3>
            <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
              Scan a restaurant for near-duplicate products on no-articleId platforms (same supplier, similar name, similar price).
              Combining renames all variants to one name across order history, cost price, inventory and price alerts, and rewrites future invoices.
            </p>
          </div>

          {/* Restaurant picker */}
          <div className="mb-4">
            <label className={cn(secondaryText, 'text-xs block mb-1')}>Restaurant</label>
            <input
              type="text"
              placeholder="Search restaurants..."
              value={dupSearchQuery}
              onChange={(e) => setDupSearchQuery(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border mb-2',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
              {dupFilteredRestaurants.map(r => (
                <button
                  key={r.id}
                  onClick={() => setDupRestaurantId(r.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 transition-colors',
                    smallTextSize,
                    dupRestaurantId === r.id
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  <div className={cn(primaryText, semiBoldText)}>{r.name}</div>
                  <div className={cn(secondaryText, 'text-xs')}>{r.ownerEmail}</div>
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleScanDuplicates(dupRestaurantId)}
                disabled={!dupRestaurantId || dupLoading}
                className={cn(
                  'px-4 py-2 rounded-lg transition-colors',
                  'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  smallTextSize, semiBoldText
                )}
              >
                {dupLoading ? 'Scanning...' : 'Scan for Duplicates'}
              </button>
              <button
                onClick={() => handleScanDuplicates()}
                disabled={dupLoading}
                title="Scan every restaurant at once (slower)"
                className={cn(
                  'px-4 py-2 rounded-lg transition-colors',
                  'bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 text-white dark:text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  smallTextSize, semiBoldText
                )}
              >
                {dupLoading ? 'Scanning...' : 'Scan All Restaurants'}
              </button>
            </div>
          </div>

          {dupError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm mb-4">
              {dupError}
            </div>
          )}

          {dupResult && (
            <DuplicateProductsView result={dupResult} setResult={setDupResult} />
          )}
        </div>

        {/* Moët scan-parse duplicates — article-id keyed, so a separate flow from the card above. */}
        <MoetDuplicatesCard restaurants={restaurants} />
        </>)}

        {view === 'aiChecker' && (<>
          <AICheckerView t={t} />
        </>)}

        {view === 'export' && (<>
        {/* Data Export Card */}
        <div className={cn(cardClass, 'p-6')}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={cn(primaryText, semiBoldText)}>Data Export</h3>
              <p className={cn(secondaryText, smallTextSize, 'mt-1')}>
                Export a full markdown report (overview, revenue, spending, cost price) for a restaurant over a date range
              </p>
            </div>
          </div>

          {/* Restaurant selector */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search restaurants..."
              value={exportSearchQuery}
              onChange={(e) => setExportSearchQuery(e.target.value)}
              className={cn(
                'w-full px-3 py-2 rounded-lg border mb-2',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                primaryText, smallTextSize
              )}
            />
            <div className="max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg mb-3">
              {exportFilteredRestaurants.map(r => (
                <button
                  key={r.id}
                  onClick={() => setExportRestaurantId(r.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 transition-colors',
                    smallTextSize,
                    exportRestaurantId === r.id
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  <div className={cn(primaryText, semiBoldText)}>{r.name}</div>
                  <div className={cn(secondaryText, 'text-xs')}>{r.ownerEmail}</div>
                </button>
              ))}
            </div>

            {/* Date range */}
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>Start Date</label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
              <div className="flex-1">
                <label className={cn(secondaryText, 'text-xs block mb-1')}>End Date</label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  className={cn(
                    'w-full px-3 py-2 rounded-lg border',
                    'border-gray-300 dark:border-gray-600',
                    'bg-white dark:bg-gray-700',
                    primaryText, smallTextSize
                  )}
                />
              </div>
            </div>

            <button
              onClick={handleExport}
              disabled={!exportRestaurantId || !exportStartDate || !exportEndDate || exportLoading}
              className={cn(
                'px-4 py-2 rounded-lg transition-colors',
                'bg-purple-600 dark:bg-purple-600 hover:bg-purple-700 dark:hover:bg-purple-700 text-white dark:text-white',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                smallTextSize, semiBoldText
              )}
            >
              {exportLoading ? 'Exporting...' : 'Export Data'}
            </button>
          </div>

          {/* Result message */}
          {exportMessage && (
            <div className={cn(
              'p-3 rounded-lg text-sm mb-4',
              exportMessage.type === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
            )}>
              {exportMessage.text}
            </div>
          )}
        </div>

        </>)}
      </div>
    </AdminLayout>
  );
};

export default AdminTestingPage;
